// V1.0.64: Quản lý thiết bị ghi âm (Admin)
import { useState } from 'react';
import {
  Table, Button, Tag, Space, Modal, Form, Input, InputNumber,
  Switch, App as AntdApp, Popconfirm, Select, Empty, Card, Alert, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Cpu, CheckCircle, Trash2, Edit2, Key, Power, RefreshCw } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { voiceService, type RecordingDevice } from '@/services/voiceService';
import { departmentService } from '@/services/categoryService';
import dayjs from 'dayjs';

function formatRelative(d?: string | null): string {
  if (!d) return 'Chưa có';
  const diffMs = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Vừa xong';
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ngày trước`;
  return dayjs(d).format('DD/MM/YYYY HH:mm');
}

export default function RecordingDevicesAdminPage() {
  const { message, modal } = AntdApp.useApp();
  const qc = useQueryClient();
  const [approveOpen, setApproveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<RecordingDevice | null>(null);
  const [tokenModal, setTokenModal] = useState<{ open: boolean; token: string }>({ open: false, token: '' });
  const [approveForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['recording-devices'],
    queryFn: () => voiceService.listDevices(),
    refetchInterval: 60_000,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.list(),
  });

  const pendingDevices = devices.filter(d => !d.isApproved);
  const approvedDevices = devices.filter(d => d.isApproved);

  const approveMutation = useMutation({
    mutationFn: (vars: { id: string; values: any }) => voiceService.approveDevice(vars.id, vars.values),
    onSuccess: (res) => {
      message.success('Đã phê duyệt thiết bị');
      setTokenModal({ open: true, token: res.deviceToken });
      setApproveOpen(false);
      approveForm.resetFields();
      qc.invalidateQueries({ queryKey: ['recording-devices'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Phê duyệt thất bại'),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; values: any }) => voiceService.updateDevice(vars.id, vars.values),
    onSuccess: () => {
      message.success('Đã cập nhật');
      setEditOpen(false);
      qc.invalidateQueries({ queryKey: ['recording-devices'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Cập nhật thất bại'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => voiceService.deleteDevice(id),
    onSuccess: () => {
      message.success('Đã xóa thiết bị');
      qc.invalidateQueries({ queryKey: ['recording-devices'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Xóa thất bại'),
  });

  const openApprove = (device: RecordingDevice) => {
    setSelected(device);
    approveForm.setFieldsValue({
      deviceCode: device.deviceCode.startsWith('PENDING-') ? '' : device.deviceCode,
      deviceName: device.deviceName,
      location: device.location,
      departmentId: device.departmentId,
      chunkDurationSec: device.chunkDurationSec ?? 1200,
      autoTranscribe: device.autoTranscribe ?? true,
    });
    setApproveOpen(true);
  };

  const openEdit = (device: RecordingDevice) => {
    setSelected(device);
    editForm.setFieldsValue({
      deviceName: device.deviceName,
      location: device.location,
      departmentId: device.departmentId,
      isActive: device.isActive,
      chunkDurationSec: device.chunkDurationSec,
      autoTranscribe: device.autoTranscribe,
      notes: device.notes,
    });
    setEditOpen(true);
  };

  const columns: ColumnsType<RecordingDevice> = [
    {
      title: 'Code',
      dataIndex: 'deviceCode',
      width: 130,
      render: (code: string) => (
        <span style={{ fontFamily: 'monospace', color: '#1976D2', fontSize: 12 }}>{code}</span>
      ),
    },
    {
      title: 'Tên thiết bị',
      dataIndex: 'deviceName',
      render: (name: string, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {row.hostname && <div style={{ fontSize: 11, color: '#8a95a5' }}>🖥 {row.hostname}</div>}
        </div>
      ),
    },
    {
      title: 'Vị trí · Khoa',
      render: (_: any, row) => (
        <div style={{ fontSize: 12 }}>
          {row.location && <div>📍 {row.location}</div>}
          {row.departmentName && <div style={{ color: '#7d8a98' }}>🏥 {row.departmentName}</div>}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      width: 120,
      render: (_: any, row) => {
        if (!row.isApproved) return <Tag color="orange">Chờ duyệt</Tag>;
        if (!row.isActive) return <Tag color="default">Tạm dừng</Tag>;
        const lastChunkMs = row.lastChunkAt ? Date.now() - new Date(row.lastChunkAt).getTime() : Infinity;
        if (lastChunkMs < 15 * 60_000) return <Tag color="green">🟢 Online</Tag>;
        if (lastChunkMs < 2 * 60 * 60_000) return <Tag color="orange">🟡 Chậm</Tag>;
        return <Tag color="red">🔴 Offline</Tag>;
      },
    },
    {
      title: 'Hoạt động gần nhất',
      width: 160,
      render: (_: any, row) => (
        <div style={{ fontSize: 12 }}>
          <div>Seen: {formatRelative(row.lastSeenAt)}</div>
          <div style={{ color: '#7d8a98' }}>Chunk: {formatRelative(row.lastChunkAt)}</div>
        </div>
      ),
    },
    {
      title: 'Hành động',
      width: 180,
      render: (_: any, row) => (
        <Space size={4}>
          {!row.isApproved ? (
            <Button type="primary" size="small" icon={<CheckCircle size={14} />} onClick={() => openApprove(row)}>
              Phê duyệt
            </Button>
          ) : (
            <>
              <Tooltip title="Sửa thông tin">
                <Button size="small" icon={<Edit2 size={14} />} onClick={() => openEdit(row)} />
              </Tooltip>
              <Tooltip title={row.isActive ? 'Tạm dừng' : 'Kích hoạt lại'}>
                <Button
                  size="small"
                  icon={<Power size={14} />}
                  danger={row.isActive}
                  onClick={() => updateMutation.mutate({ id: row.id, values: { isActive: !row.isActive } })}
                />
              </Tooltip>
            </>
          )}
          <Popconfirm
            title="Xóa thiết bị?"
            description="Thao tác này không thể phục hồi"
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(row.id)}
          >
            <Button size="small" danger icon={<Trash2 size={14} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0 }}>
            <Cpu size={20} style={{ verticalAlign: -3, marginRight: 8, color: '#6699CC' }} />
            Quản lý thiết bị ghi âm
          </h2>
          <div style={{ color: '#7d8a98', fontSize: 13, marginTop: 4 }}>
            Thiết bị tự đăng ký khi service cài đặt khởi động. Admin cần phê duyệt + gán mã + vị trí.
          </div>
        </div>
        <Button icon={<RefreshCw size={14} />} onClick={() => qc.invalidateQueries({ queryKey: ['recording-devices'] })}>
          Tải lại
        </Button>
      </div>

      {pendingDevices.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Có ${pendingDevices.length} thiết bị mới đăng ký, đang chờ phê duyệt`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title={`Chờ phê duyệt (${pendingDevices.length})`} size="small" style={{ marginBottom: 16 }}>
        {pendingDevices.length === 0 ? (
          <Empty description="Không có thiết bị nào chờ phê duyệt" />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={pendingDevices}
            loading={isLoading}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      <Card title={`Thiết bị đã phê duyệt (${approvedDevices.length})`} size="small">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={approvedDevices}
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
        />
      </Card>

      {/* Modal phê duyệt */}
      <Modal
        title={`Phê duyệt thiết bị: ${selected?.hostname ?? '?'}`}
        open={approveOpen}
        onCancel={() => { setApproveOpen(false); approveForm.resetFields(); }}
        onOk={() => approveForm.submit()}
        confirmLoading={approveMutation.isPending}
        okText="Phê duyệt"
        cancelText="Hủy"
        width={560}
      >
        <Alert
          type="info"
          showIcon
          message="Sau khi phê duyệt, thiết bị sẽ tự nhận token và bắt đầu ghi âm + upload."
          style={{ marginBottom: 16 }}
        />
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={(values) => selected && approveMutation.mutate({ id: selected.id, values })}
        >
          <Form.Item
            label="Mã thiết bị (Code)"
            name="deviceCode"
            rules={[{ required: true, message: 'Bắt buộc' }, { pattern: /^[A-Z0-9-]+$/i, message: 'Chỉ chữ, số và dấu -' }]}
            extra="Ví dụ: LT-01, PK-N1-01, TIEPDON-01 (in hoa)"
          >
            <Input placeholder="LT-01" style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item label="Tên hiển thị" name="deviceName" rules={[{ required: true }]}>
            <Input placeholder="VD: Lễ tân tầng 1 - quầy 1" />
          </Form.Item>
          <Form.Item label="Vị trí lắp đặt" name="location">
            <Input placeholder="VD: Tầng 1 - sảnh chính" />
          </Form.Item>
          <Form.Item label="Khoa/Phòng" name="departmentId">
            <Select
              showSearch
              allowClear
              placeholder="Chọn khoa"
              optionFilterProp="label"
              options={departments.map((d: any) => ({ value: d.id, label: d.name }))}
            />
          </Form.Item>
          <Form.Item label="Thời lượng mỗi chunk (giây)" name="chunkDurationSec">
            <InputNumber min={60} max={3600} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Tự động transcribe" name="autoTranscribe" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal sửa */}
      <Modal
        title={`Sửa thông tin: ${selected?.deviceCode ?? ''}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isPending}
        okText="Lưu"
        cancelText="Hủy"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => selected && updateMutation.mutate({ id: selected.id, values })}
        >
          <Form.Item label="Tên hiển thị" name="deviceName" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Vị trí" name="location"><Input /></Form.Item>
          <Form.Item label="Khoa/Phòng" name="departmentId">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={departments.map((d: any) => ({ value: d.id, label: d.name }))}
            />
          </Form.Item>
          <Form.Item label="Hoạt động" name="isActive" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item label="Thời lượng chunk (giây)" name="chunkDurationSec">
            <InputNumber min={60} max={3600} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Auto transcribe" name="autoTranscribe" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item label="Ghi chú" name="notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Modal hiển thị token sau khi approve */}
      <Modal
        title="✅ Đã phê duyệt thiết bị"
        open={tokenModal.open}
        onCancel={() => setTokenModal({ open: false, token: '' })}
        footer={[
          <Button key="copy" type="primary" icon={<Key size={14} />} onClick={() => {
            navigator.clipboard.writeText(tokenModal.token);
            message.success('Đã copy token');
          }}>
            Copy Token
          </Button>,
          <Button key="close" onClick={() => setTokenModal({ open: false, token: '' })}>Đóng</Button>,
        ]}
      >
        <Alert
          type="success"
          showIcon
          message="Thiết bị đã được phê duyệt thành công"
          description="Service trên máy đó sẽ tự fetch token sau ~1 phút. Token bên dưới chỉ để IT lưu phòng trường hợp cần config thủ công."
          style={{ marginBottom: 16 }}
        />
        <div style={{
          padding: 10, background: '#f5f5f5', borderRadius: 4,
          fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all',
        }}>
          {tokenModal.token}
        </div>
      </Modal>
    </div>
  );
}
